import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTitleContext } from '../../store/Page/TitleContext';
import { _links } from './_links';
import { buildMenuTitle } from './findMenuItem';
import type { MenuItemNode } from './MenuItem';

const APP_NAME = 'Callmart by Unetiq';

/** Human-readable titles for routes that are not part of the sidebar menu. */
const STATIC_TITLES: Record<string, string> = {
    profile: 'Profil',
    impressum: 'Impressum',
    dataprivacy: 'Datenschutz',
};

const resolvePageTitle = (pathname: string, getLabel: (item: MenuItemNode) => string): string | undefined => {
    // Root renders the welcome screen (HomePage), not the dashboard, so show
    // just the app name. Without this, the menu lookup's sidebar-only "empty
    // path -> dashboard" fallback would mislabel the tab as "Dashboard".
    if (pathname === '/') return undefined;
    const menuTitle = buildMenuTitle(_links, pathname, getLabel);
    if (menuTitle) return menuTitle;
    return STATIC_TITLES[pathname.replace(/^\//, '')];
};

/**
 * Keeps the browser tab title in sync with the active route. A page-set title
 * (via TitleContext, e.g. "Forecast #123") takes precedence; otherwise the
 * active menu hierarchy is used (e.g. "Offered / Bidding"). Falls back to the
 * bare app name for unmapped routes. Renders nothing.
 */
const DocumentTitle = () => {
    const { pathname } = useLocation();
    const { title } = useTitleContext();
    const { t } = useTranslation('navigation');

    useEffect(() => {
        const pageTitle =
            title ?? resolvePageTitle(pathname, (item) => (item.value ? t(item.value) : (item.text ?? '')));
        document.title = pageTitle ? `${pageTitle} - ${APP_NAME}` : APP_NAME;
    }, [title, pathname, t]);

    return null;
};

export default DocumentTitle;
